import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, CheckCheck, Mail, ClipboardList, X, Settings2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, dateTime } from '../lib/api';
import {
  getNotificationPreferences,
  setNotificationPreferences,
  getNotificationState,
  setNotificationState,
  getSystemNotificationPermission,
  requestSystemNotificationPermission,
  sendSystemNotification,
  sendTestSystemNotification,
  notificationRuntime,
} from '../lib/system-notifications';

const newest=(a,b)=>String(b.occurred_at||'').localeCompare(String(a.occurred_at||''));

function normaliseCase(row){
  const ref=row.case_reference||row.id||'request';
  return {
    id:`case:${row.id||ref}`,
    type:'cases',
    title:String(row.case_type||'').toLowerCase()==='complaint'?'Complaint assigned to you':'Request assigned to you',
    body:`${ref} · ${String(row.priority||'normal').replaceAll('_',' ')} priority`,
    occurred_at:row.created_at||row.updated_at||new Date().toISOString(),
    route:`/cases/${encodeURIComponent(row.id||'')}`,
    icon:'case',
    systemBody:`Customer Operations work item ${ref} has been assigned to you.`,
  };
}

function normaliseTask(row){
  return {
    id:`task:${row.id}`,
    type:'tasks',
    title:'Task assigned to you',
    body:'A Customer Operations task requires your attention.',
    occurred_at:row.created_at||row.updated_at||row.due_at||new Date().toISOString(),
    route:'/tasks',
    icon:'task',
    systemBody:'A Customer Operations task has been assigned to you.',
  };
}

function normaliseEmail(row){
  return {
    id:`email:${row.id}`,
    type:'email',
    title:'New inbound email',
    body:`Received in ${row.mailbox_name||row.mailbox_email||'Customer Operations'}`,
    occurred_at:row.occurred_at||row.received_at||row.created_at||new Date().toISOString(),
    route:'/inbox',
    icon:'email',
    systemBody:'A new inbound email has arrived in Customer Operations.',
  };
}

async function loadNotificationItems(preferences){
  const jobs=[];
  if(preferences.cases)jobs.push(api('/api/cases?queue=assigned_to_me').then(body=>(body.cases||[]).map(normaliseCase)));
  if(preferences.tasks)jobs.push(api('/api/crm/tasks').then(body=>(body.tasks||[]).filter(row=>!['completed','closed','cancelled'].includes(String(row.status||'').toLowerCase())).map(normaliseTask)));
  if(preferences.email)jobs.push(api('/api/email/inbox?direction=inbound&limit=60').then(body=>(body.messages||[]).filter(row=>!row.is_read).map(normaliseEmail)));
  const settled=await Promise.allSettled(jobs);
  return settled.flatMap(result=>result.status==='fulfilled'?result.value:[]).sort(newest).slice(0,100);
}

export default function NotificationCentre({staff}){
  const navigate=useNavigate();
  const staffId=staff?.id||staff?.email||'staff';
  const[open,setOpen]=useState(false);
  const[items,setItems]=useState([]);
  const[prefs,setPrefs]=useState(()=>getNotificationPreferences());
  const[state,setState]=useState(()=>getNotificationState(staffId));
  const[permission,setPermission]=useState('prompt');
  const[busy,setBusy]=useState(false);
  const[permissionNotice,setPermissionNotice]=useState('');
  const pollBusy=useRef(false);

  useEffect(()=>{
    setState(getNotificationState(staffId));
  },[staffId]);

  const refreshPermission=useCallback(()=>getSystemNotificationPermission().then(setPermission),[]);
  useEffect(()=>{refreshPermission();},[refreshPermission]);

  useEffect(()=>{
    const changed=event=>setPrefs(event.detail||getNotificationPreferences());
    window.addEventListener('crm-notification-preferences-change',changed);
    return()=>window.removeEventListener('crm-notification-preferences-change',changed);
  },[]);

  const poll=useCallback(async()=>{
    if(!staff?.id||pollBusy.current)return;
    pollBusy.current=true;
    try{
      const currentPrefs=getNotificationPreferences();
      const nextItems=await loadNotificationItems(currentPrefs);
      setItems(nextItems);
      let stored=getNotificationState(staffId);
      const currentIds=nextItems.map(item=>item.id);

      if(!stored.initialised){
        stored=setNotificationState(staffId,{initialised:true,seen:currentIds,read:currentIds});
        setState(stored);
        return;
      }

      const seen=new Set(stored.seen||[]);
      const fresh=nextItems.filter(item=>!seen.has(item.id));
      const nextSeen=[...(stored.seen||[]),...fresh.map(item=>item.id)];
      stored=setNotificationState(staffId,{...stored,seen:nextSeen});
      setState(stored);

      if(currentPrefs.enabled&&fresh.length){
        const allowed=await getSystemNotificationPermission();
        setPermission(allowed);
        if(allowed==='granted'){
          const individual=fresh.slice(0,3);
          for(const item of individual){
            await sendSystemNotification({
              title:item.title,
              body:item.systemBody,
              tag:item.id,
              route:item.route,
            });
          }
          if(fresh.length>3){
            await sendSystemNotification({
              title:'Sousa Murray Customer Operations',
              body:`${fresh.length} new work notifications require your attention.`,
              tag:'smco-notification-summary',
              route:'/',
            });
          }
        }
      }
    }finally{
      pollBusy.current=false;
    }
  },[staff?.id,staffId]);

  useEffect(()=>{
    if(!staff?.id)return undefined;
    void poll();
    const timer=window.setInterval(()=>void poll(),45000);
    const visible=()=>{if(document.visibilityState==='visible')void poll();};
    document.addEventListener('visibilitychange',visible);
    return()=>{window.clearInterval(timer);document.removeEventListener('visibilitychange',visible);};
  },[staff?.id,poll]);

  const readSet=useMemo(()=>new Set(state.read||[]),[state.read]);
  const unread=useMemo(()=>items.filter(item=>!readSet.has(item.id)).length,[items,readSet]);

  const persistRead=ids=>{
    const next=setNotificationState(staffId,{...state,read:[...(state.read||[]),...ids]});
    setState(next);
  };

  const openItem=item=>{
    persistRead([item.id]);
    setOpen(false);
    navigate(item.route);
  };

  const enable=async()=>{
    setBusy(true);
    setPermissionNotice('');
    try{
      const result=await requestSystemNotificationPermission();
      setPermission(result);
      if(result==='granted'){
        const next=setNotificationPreferences({enabled:true});
        setPrefs(next);
        const sent=await sendTestSystemNotification();
        setPermissionNotice(sent?'Computer notifications enabled.':'Permission granted, but the test notification could not be delivered.');
      }else{
        const next=setNotificationPreferences({enabled:false});
        setPrefs(next);
        if(result==='denied')setPermissionNotice('Notifications are blocked in Microsoft Edge/site permissions.');
        else if(result==='blocked')setPermissionNotice('The notification permission prompt did not complete. Check Edge site permissions for this CRM.');
        else if(result==='prompt')setPermissionNotice('Choose Allow when Edge asks for notification permission.');
        else setPermissionNotice('System notifications are unavailable here.');
      }
    }catch(error){
      const next=setNotificationPreferences({enabled:false});
      setPrefs(next);
      setPermissionNotice(error?.message||'Could not enable computer notifications.');
    }finally{setBusy(false)}
  };

  const disable=()=>{
    const next=setNotificationPreferences({enabled:false});
    setPrefs(next);
  };

  const runtime=notificationRuntime();
  return <div className="notification-centre">
    <button className="icon-button notification-bell" aria-label={unread?`Notifications, ${unread} unread`:'Notifications'} onClick={()=>setOpen(value=>!value)}>
      <Bell size={19}/>
      {unread>0&&<span className="notification-count">{unread>99?'99+':unread}</span>}
    </button>
    {open&&<div className="notification-popover" role="dialog" aria-label="Customer Operations notifications">
      <div className="notification-popover-head">
        <div><strong>Notifications</strong><small>{prefs.enabled&&permission==='granted'?'Computer notifications on':'Customer Operations alerts'}</small></div>
        <button className="icon-button" onClick={()=>setOpen(false)} aria-label="Close notifications"><X size={16}/></button>
      </div>
      <div className="notification-system-strip">
        <div><span className={prefs.enabled&&permission==='granted'?'notification-dot-state on':'notification-dot-state'}/><span><strong>{prefs.enabled&&permission==='granted'?'Computer notifications enabled':'Computer notifications off'}</strong><small>{runtime==='tauri'?'Native Windows notifications':runtime==='browser'?'Windows/browser notifications':'Notifications are not supported in this environment'}</small></span></div>
        {runtime!=='unsupported'&&(prefs.enabled&&permission==='granted'
          ?<button onClick={disable}>Turn off</button>
          :<button onClick={enable} disabled={busy}>{busy?'Enabling…':'Enable'}</button>)}
      </div>
      {permissionNotice&&<div className={`notification-permission-notice ${permission==='granted'?'success':'error'}`}>{permissionNotice}</div>}
      <div className="notification-toolbar">
        <button onClick={()=>persistRead(items.map(item=>item.id))} disabled={!unread}><CheckCheck size={14}/>Mark all read</button>
        <button onClick={()=>{setOpen(false);navigate('/settings#notifications')}}><Settings2 size={14}/>Settings</button>
      </div>
      <div className="notification-list">
        {items.length?items.slice(0,30).map(item=>{
          const Icon=item.icon==='email'?Mail:ClipboardList;
          const unreadItem=!readSet.has(item.id);
          return <button key={item.id} className={`notification-item ${unreadItem?'unread':''}`} onClick={()=>openItem(item)}>
            <span className="notification-item-icon"><Icon size={16}/></span>
            <span className="notification-item-copy"><strong>{item.title}</strong><span>{item.body}</span><small>{dateTime(item.occurred_at)}</small></span>
            {unreadItem&&<span className="notification-unread-dot"/>}
          </button>;
        }):<div className="notification-empty"><Bell size={22}/><strong>No notifications</strong><span>New work assigned to you will appear here.</span></div>}
      </div>
    </div>}
  </div>;
}
